import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SourceImages } from "@/components/create/SourceImages";
import { api } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

const TYPES = { image: ["image/png", "image/jpeg", "image/webp"], video: ["video/mp4"] };
const label = (name) => name.replace(/_urls?$/, "").replace(/_/g, " ");
const chip = (on) => cn("h-8 rounded-full px-3 text-xs transition-colors duration-200", on ? "bg-white text-ink-950" : "border border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-100");

function Setting({ name, f, value, onChange }) {
  if (f.enum) {
    return (
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={label(name)}>
        {f.enum.map((v) => <button key={String(v)} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} className={chip(value === v)} data-testid={`param-${name}-${v}`}>{String(v)}</button>)}
      </div>
    );
  }
  if (f.type === "boolean") {
    return <button type="button" role="switch" aria-checked={Boolean(value)} onClick={() => onChange(!value)} className={chip(Boolean(value))} data-testid={`param-${name}`}>{value ? "On" : "Off"}</button>;
  }
  const min = f.minimum ?? 0;
  const max = f.maximum ?? 100;
  return (
    <div className="flex items-center gap-3">
      <input type="range" min={min} max={max} step={f.type === "integer" ? 1 : (max - min) / 20} value={value ?? f.default ?? min} onChange={(e) => onChange(Number(e.target.value))}
        className="h-1 w-full max-w-xs accent-white" aria-label={label(name)} data-testid={`param-${name}`} />
      <span className="w-10 text-right font-mono text-xs tabular-nums text-zinc-300">{value ?? f.default ?? min}</span>
    </div>
  );
}

function usePrice(model, prompt, params) {
  const [price, setPrice] = useState({ loading: true });
  const key = JSON.stringify(params);
  useEffect(() => {
    let live = true;
    setPrice({ loading: true });
    const t = setTimeout(() => {
      api.post("/media/hf/quote", { model: model.id, prompt: prompt.slice(0, 400), params: JSON.parse(key) })
        .then((r) => live && setPrice(r)).catch((e) => live && setPrice({ error: e.message }));
    }, 450);
    return () => { live = false; clearTimeout(t); };
  }, [model.id, key]); // eslint-disable-line react-hooks/exhaustive-deps -- prompt text doesn't change the price
  return price;
}

// Schema-driven form for any verified catalog model: every control maps to a documented field of that model.
export function ModelForm({ model, initial = {}, presetRefs = {}, onCreated }) {
  const qc = useQueryClient();
  const fields = useMemo(() => Object.entries(model.fields), [model]);
  const settings = fields.filter(([, f]) => !f.role);
  const inputs = fields.filter(([, f]) => f.role);
  const [prompt, setPrompt] = useState(initial.prompt || "");
  const [params, setParams] = useState(() => ({ ...initial.params }));
  const [refs, setRefs] = useState(() => ({ ...presetRefs }));
  const price = usePrice(model, prompt, params);
  const missing = inputs.filter(([n]) => model.required.includes(n) && !(refs[n] || []).length).map(([n]) => label(n));
  const create = useMutation({
    mutationFn: () => api.post("/media/generations", {
      kind: model.kind, engine: "higgsfield", model: model.id, prompt: prompt.trim(), params,
      refs: Object.fromEntries(Object.entries(refs).filter(([, v]) => v.length).map(([k, v]) => [k, v.map((s) => s.id)])),
    }),
    onSuccess: (row) => {
      qc.setQueryData(["media-generations"], (d) => ({ ...d, items: [{ ...row, url: null, thumb_url: null, credits_charged: 0 }, ...(d?.items || [])] }));
      ["media-generations", "media-config", "me"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`${model.kind === "video" ? "Video" : "Image"} started`, { duration: 3000 });
      onCreated?.(row);
    },
    onError: (e) => toast.error(e.message),
  });
  if (model.unsupported_inputs.length) {
    return <p className="rounded-xl border border-white/10 p-4 text-sm text-zinc-400" data-testid="model-unsupported">This model needs {model.unsupported_inputs.map(label).join(", ")} input, which ChatSleek doesn't support yet.</p>;
  }
  const needsPrompt = model.has_prompt && model.required.includes("prompt");
  const ready = price.credits && (!needsPrompt || prompt.trim()) && !missing.length && !create.isPending;
  return (
    <div className="space-y-5" data-testid="model-form">
      {inputs.map(([name, f]) => (
        <div key={name}>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-500">{label(name)}{model.required.includes(name) ? "" : " · optional"}</p>
          <SourceImages sources={refs[name] || []} onChange={(fn) => setRefs((r) => ({ ...r, [name]: fn(r[name] || []) }))} max={f.multiple ? Math.min(f.maxItems || 4, 4) : 1}
            types={TYPES[f.role]} label={f.role} testId={`ref-${name}`} />
        </div>
      ))}
      {model.has_prompt && (
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={4000} rows={3} aria-label="Prompt" data-testid="model-prompt-input"
          placeholder={model.kind === "video" ? "Describe the shot, motion and camera…" : "Describe the image…"}
          className="w-full resize-none rounded-xl border border-white/10 bg-ink-900 p-3 text-sm leading-relaxed text-zinc-100 placeholder:text-zinc-600 focus:border-white/25 focus:outline-none" />
      )}
      {settings.map(([name, f]) => (
        <div key={name}>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-500">{label(name)}</p>
          <Setting name={name} f={f} value={params[name] ?? f.default} onChange={(v) => setParams((p) => ({ ...p, [name]: v }))} />
        </div>
      ))}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <p className="text-xs text-zinc-500" data-testid="model-price">
          {price.loading ? "Checking price…" : price.credits ? `Live price: ${formatNumber(price.credits)} credits` : price.error || "Price unavailable for these settings"}
          {missing.length > 0 && ` · add ${missing.join(", ")}`}
        </p>
        <button type="button" disabled={!ready} onClick={() => create.mutate()} data-testid="model-submit-button"
          className="flex h-11 items-center justify-center gap-2 rounded-full bg-white px-6 text-sm font-semibold text-ink-950 transition-[opacity,transform] hover:opacity-90 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-zinc-500 sm:ml-auto">
          {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Generate{price.credits ? ` · ${formatNumber(price.credits)} credits` : ""}
        </button>
      </div>
    </div>
  );
}
